import { SceneData } from './SceneExporter';
import * as THREE from 'three';
import { RepaintProgress } from '../Gui/components/panels/ProgressBar';
import { DxfWriter, point3d } from '@tarikjabiri/dxf';

/**
 * Export3DDXF.ts
 *
 * Goal:
 * - Export SceneData to DXF.
 *
 * Notes:
 * - Skip the meshes without geometry or positions.
 */

// ------------------------------------------------------------
// Small utilities
// ------------------------------------------------------------
const EPS = 1e-12;

function isFiniteVec3(v: THREE.Vector3) {
    return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

function almostEqual(a: THREE.Vector3, b: THREE.Vector3) {
    return Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS && Math.abs(a.z - b.z) < EPS;
}

function triArea2(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3) {
    // area * 2 = |(b-a) x (c-a)|
    const ab = new THREE.Vector3().subVectors(b, a);
    const ac = new THREE.Vector3().subVectors(c, a);
    const cross = new THREE.Vector3().crossVectors(ab, ac);
    return cross.length();
}

// ------------------------------------------------------------
// Main export function
// ------------------------------------------------------------
export async function Export3DDXF(
    scenedata: SceneData,
    setProgress: (p: number | undefined) => void,
    orientation3d: 'Y_Up' | 'Z_Up' = 'Y_Up'
): Promise<{ formattedData: Uint8Array; extension?: string }> {
    console.log('Exporting scene to DXF format');

    const dxf = new DxfWriter();

    const isValidMesh = (obj: THREE.Object3D): obj is THREE.Mesh => {
        const geom = (obj as any).geometry as THREE.BufferGeometry | undefined;
        if (!geom || geom.isBufferGeometry !== true) return false;
        const pos = geom.getAttribute?.('position') as THREE.BufferAttribute | undefined;
        return !!pos && typeof pos.count === 'number' && pos.count >= 3;
    };

    const toDxfPoint = (p: THREE.Vector3) => {
        if (orientation3d === 'Z_Up') {
            return point3d(p.x, -p.z, p.y);
        }

        return point3d(p.x, p.y, p.z);
    };

    const emitTri = (layerName: string, a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3) => {
        if (!isFiniteVec3(a) || !isFiniteVec3(b) || !isFiniteVec3(c)) return;
        if (almostEqual(a, b) || almostEqual(b, c) || almostEqual(a, c)) return;
        if (triArea2(a, b, c) < EPS) return;

        dxf.add3dFace(toDxfPoint(a), toDxfPoint(b), toDxfPoint(c), toDxfPoint(c), { layerName });
    };

    let progress = 0;
    const total = Math.max(scenedata.layerMeshes.length, 1);
    setProgress((progress / total) * 80 + 10);

    for (let i = 0; i < scenedata.layerMeshes.length; i++) {
        const layerName = scenedata.layerGroupNames[i] || `Layer_${i}`;

        try {
            dxf.addLayer(layerName, 7); // 7 = default-ish color
        } catch {
            // ignore if already exists
        }

        for (const root of scenedata.layerMeshes[i]) {
            root.traverse((obj) => {
                if (!isValidMesh(obj)) return;

                const mesh = obj as THREE.Mesh;

                const geom = (mesh.geometry as THREE.BufferGeometry).clone();

                const pos = geom.getAttribute('position') as THREE.BufferAttribute;
                const idx = geom.getIndex();

                const v = (k: number) => new THREE.Vector3(pos.getX(k), pos.getY(k), pos.getZ(k));

                if (idx) {
                    // Indexed triangles
                    for (let t = 0; t + 2 < idx.count; t += 3) {
                        const ia = idx.getX(t);
                        const ib = idx.getX(t + 1);
                        const ic = idx.getX(t + 2);
                        emitTri(layerName, v(ia), v(ib), v(ic));
                    }
                } else {
                    // Non-indexed triangles
                    const triCount = Math.floor(pos.count / 3);
                    for (let t = 0; t < triCount; t++) {
                        const base = t * 3;
                        emitTri(layerName, v(base), v(base + 1), v(base + 2));
                    }
                }
            });
        }

        progress++;
        setProgress((progress / total) * 80 + 10);
        await RepaintProgress();
    }

    const dxfString = dxf.stringify();
    return { formattedData: new TextEncoder().encode(dxfString), extension: 'dxf' };
}
